import { DateFormats, formatDate, sortAsc } from '@strata/helper-functions';
import { ChatWithConversationPane, SeenIndicator } from '@strata/react-chat';
import { FormPageContainer } from '@strata/react-form-header-and-content';
import { GridWithFilters, PrimaryData, SecondaryData } from '@strata/react-grid';
import { LastUpdatedIcon } from '@strata/react-status-icons';
import { CountdownTimer } from '@strata/react-timers';
import classNames from 'classnames';
import dayjs from 'dayjs';
import { useNavigate } from 'react-router';

import { AppRoutes } from 'services/routes';
import { MESSAGES_LOCATION_HASH } from 'services/userSupportHelpers';
import { useGetAllSuppliersQuery } from 'store/api/referenceApi';
import {
    useGetConversationFilterCategoriesQuery,
    useGetPagedConversationsQuery,
} from 'store/api/userSupportApi';
import { Grids, useGridState } from 'store/grids/gridsSlice';

const initialSortProperties = [{ id: 'lastUpdated', desc: true }];

const CAUTION_TIME_THRESHOLD_SECONDS = -1 * 60 * 60 * 3; // 3 hours past
const WARNING_TIME_THRESHOLD_SECONDS = -1 * 60 * 60 * 1.5; // 1.5 hours past

const columns = [
    {
        accessorKey: 'isUnread',
        enableSorting: false,
        header: '',
        size: 1,
        cell: ({ getValue }) => (
            <SeenIndicator seen={!getValue()} size={SeenIndicator.sizes.LARGE} />
        ),
    },
    {
        accessorKey: 'subject',
        enableSorting: false,
        header: 'Subject',
        size: 150,
        cell: ({ getValue }) => (
            <div
                className={classNames({
                    'sds_messages--unread': getValue()?.isUnread,
                })}
            >
                <PrimaryData data={getValue()?.title} />
            </div>
        ),
    },
    {
        accessorKey: 'detail',
        enableSorting: false,
        header: 'Detail',
        size: 150,
    },
    {
        accessorKey: 'timer',
        enableSorting: false,
        header: 'Timer',
        size: 100,
        cell: ({ row }) => {
            if (
                row.original.lastUpdatedByFmhUser ||
                row.original.conversationStatus ===
                    ChatWithConversationPane.ConversationStatus.CLOSED
            ) {
                return <SecondaryData data={undefined} />;
            }
            // We're using the countdown timer as a countup timer here to take advantage of the thresholds.
            // If this becomes a pattern we want to support outside of Agora, we can update the CountuptTimer component
            return (
                <CountdownTimer
                    endTime={dayjs(row.original.lastUpdated)}
                    cautionTimeThresholdSeconds={CAUTION_TIME_THRESHOLD_SECONDS}
                    warningTimeThresholdSeconds={WARNING_TIME_THRESHOLD_SECONDS}
                    showTimePastEnded
                />
            );
        },
    },
    {
        accessorKey: 'lastUpdated',
        enableSorting: true,
        header: 'Last Updated',
        size: 100,
        cell: ({ getValue, row }) => (
            <SecondaryData
                data={
                    <div className="flex__gap--small" style={{ alignItems: 'center' }}>
                        <LastUpdatedIcon lastUpdatedByFmhUser={row.original.lastUpdatedByFmhUser} />
                        {formatDate(getValue(), DateFormats.DATE_TIME)}
                    </div>
                }
                customTooltipContent={
                    <>
                        <div>
                            <span className="font-semi-bold--capitalize">User: </span>
                            {row.original.lastUpdatedBy}
                        </div>
                        <div>
                            <span className="font-semi-bold--capitalize">Date: </span>
                            {formatDate(getValue(), DateFormats.DATE_TIME)}
                        </div>
                    </>
                }
            />
        ),
    },
    {
        accessorKey: 'supplierName',
        enableSorting: false,
        header: 'Pharmacy',
        size: 100,
    },
    {
        accessorKey: 'assignedTo',
        enableSorting: false,
        header: 'Assigned To',
        size: 100,
        cell: ({ getValue }) => (
            <SecondaryData
                data={
                    <span
                        className={classNames({
                            'sds_messages__unassigned-label': !getValue(),
                        })}
                    >
                        {getValue() ?? 'Unassigned'}
                    </span>
                }
            />
        ),
    },
];

const getSupplierName = (npi, supplierMap) =>
    !(npi && supplierMap) ? undefined : (supplierMap[npi]?.name ?? 'Unknown Supplier');

export const AllMessages = () => {
    const navigate = useNavigate();

    const { data: conversationFilterCategories } = useGetConversationFilterCategoriesQuery();

    const {
        activeFilters,
        filterPaneWidth,
        page,
        search,
        sort,
        updateFilterPaneWidth,
        updateGridActiveFilters,
        updateGridSearch,
        updateGridSort,
        updateCurrentPage,
    } = useGridState(Grids.CONVERSATIONS, initialSortProperties);

    const { data: allSuppliersData } = useGetAllSuppliersQuery();
    const { supplierMap } = allSuppliersData ?? {};

    const getPagedConversationsParams = { page, search, sort, activeFilters };
    const { data: firstPageData, isFetching: isFirstPageDataFetching } =
        useGetPagedConversationsQuery({ ...getPagedConversationsParams, page: 0 });
    const { data: currentPageData, isFetching: isCurrentPageDataFetching } =
        useGetPagedConversationsQuery(getPagedConversationsParams);

    const mappedConversations = currentPageData?.conversations?.map((conversation) => ({
        ...conversation,
        supplierName: getSupplierName(conversation.supplierNpi, supplierMap),
    }));

    const filterCategories = conversationFilterCategories?.map((category) => {
        if (category.key === 'supplier_npi') {
            return {
                ...category,
                customValues: category.customValues
                    ?.map((value) => {
                        const supplierName = getSupplierName(value.value, supplierMap);
                        const text =
                            supplierName === 'Unknown Supplier'
                                ? `${supplierName} (${value.value})`
                                : supplierName;
                        return {
                            text,
                            value: value.value,
                        };
                    })
                    .sort((a, b) => sortAsc(a.text, b.text)),
            };
        }
        return category;
    });

    return (
        <FormPageContainer>
            <GridWithFilters
                title="All Messages"
                data={mappedConversations}
                isLoading={isFirstPageDataFetching || isCurrentPageDataFetching}
                sortProperties={sort}
                onSort={updateGridSort}
                enableSorting
                manualSorting
                enableSearch
                searchText={search}
                onSearch={updateGridSearch}
                filterCategories={filterCategories}
                activeFilters={activeFilters}
                onFilter={updateGridActiveFilters}
                filterPaneRenderWidth={filterPaneWidth}
                onFilterPaneResize={updateFilterPaneWidth}
                currentPage={page}
                totalCount={firstPageData?.count}
                onPageChange={updateCurrentPage}
                onRowClick={(rowData) => {
                    navigate(
                        {
                            pathname: AppRoutes.referralRedirect(rowData.referralId),
                            hash: MESSAGES_LOCATION_HASH,
                        },
                        {
                            state: {
                                conversationId: rowData.conversationId,
                                backToDestination: {
                                    text: 'Back to All Messages',
                                    route: AppRoutes.allMessages,
                                },
                            },
                        },
                    );
                }}
                columns={columns}
            />
        </FormPageContainer>
    );
};
